import { CHANGE_REQUEST_STATUSES, CLOSED_CHANGE_STATUSES, type ChangeActor, type ChangeEventKind, type ChangeRequest, type ChangeRequestStatus, type Requirement, type RequirementVersion } from '../../types.js'

/** Where the Changes page lives, and the query parameter naming the change open on it (the admin router matches whole paths only). */
export const CHANGES_PATH = '/admin/changes'
export const CHANGE_PARAM = 'change'

export function changeHref(changeRequestId: string): string {
  return `${CHANGES_PATH}?${CHANGE_PARAM}=${encodeURIComponent(changeRequestId)}`
}

export const CHANGE_STATUS_LABEL: Record<ChangeRequestStatus, string> = {
  draft: 'Draft',
  proposed: 'Proposed',
  approved: 'Approved',
  ready_for_review: 'In review',
  accepted: 'Accepted',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

export const CHANGE_STATUS_CLASS: Record<ChangeRequestStatus, string> = {
  draft: 'bg-marine-lt text-marine-dk border-marine/30',
  proposed: 'bg-gold/15 text-ink border-gold/50',
  approved: 'bg-marine text-white border-marine',
  ready_for_review: 'bg-gold/15 text-ink border-gold/50',
  accepted: 'bg-green/15 text-green border-green/40',
  rejected: 'bg-silver-lt text-ink-mid border-silver/60',
  cancelled: 'bg-silver-lt text-ink-mid border-silver/60',
}

export const CHANGE_EVENT_LABEL: Record<ChangeEventKind, string> = {
  created: 'Accepted for development',
  requirement_saved: 'Requirement saved',
  submitted: 'Submitted for approval',
  returned: 'Returned to draft',
  approved: 'Approved to send',
  pull_request_recorded: 'Pull request recorded',
  sent_back: 'Sent back',
  accepted: 'Accepted',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

/** The statuses in which the next step is the root's: gate 2 and gate 3. */
export const WAITING_ON_ROOT: readonly ChangeRequestStatus[] = ['proposed', 'approved', 'ready_for_review']

export function isClosedChange(change: Pick<ChangeRequest, 'status'>): boolean {
  return CLOSED_CHANGE_STATUSES.includes(change.status)
}

/** The requirement version the change stands on now. */
export function latestRequirement(change: Pick<ChangeRequest, 'versions'>): RequirementVersion | null {
  return change.versions[change.versions.length - 1] ?? null
}

export function changeTitle(change: Pick<ChangeRequest, 'versions' | 'changeRequestId'>): string {
  const title = latestRequirement(change)?.requirement.title.trim() ?? ''
  return title === '' ? `Untitled change ${change.changeRequestId}` : title
}

export function actorLabel(actor: ChangeActor): string {
  return actor.name ?? actor.username
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7)
}

export interface ChangeSummary {
  total: number
  byStatus: Record<ChangeRequestStatus, number>
  /** Proposed or in review: a gate only the root can pass. */
  waitingOnRoot: number
  open: number
}

/** The counts the page opens with. Approved is the root's to run too, but not a decision, so "waiting on the root" counts the two gates. */
export function summarizeChanges(changes: readonly Pick<ChangeRequest, 'status'>[]): ChangeSummary {
  const byStatus = Object.fromEntries(CHANGE_REQUEST_STATUSES.map((status) => [status, 0])) as Record<ChangeRequestStatus, number>
  const counted = changes.reduce<Record<ChangeRequestStatus, number>>((counts, change) => ({ ...counts, [change.status]: counts[change.status] + 1 }), byStatus)
  return {
    total: changes.length,
    byStatus: counted,
    waitingOnRoot: counted.proposed + counted.ready_for_review,
    open: changes.filter((change) => !isClosedChange(change)).length,
  }
}

/** A text field's value as the API takes it: trimmed, and an empty list item dropped. */
export function cleanList(items: readonly string[]): string[] {
  return items.map((item) => item.trim()).filter((item) => item !== '')
}

/** A commit SHA as the API accepts one. */
export function isSha(value: string): boolean {
  return /^[0-9a-f]{7,40}$/i.test(value.trim())
}

export function isPullRequestUrl(value: string): boolean {
  return /^https:\/\/github\.com\/[^/\s]+\/[^/\s]+\/pull\/\d+\/?$/.test(value.trim())
}

const orNull = (value: string | null): string | null => {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? null : trimmed
}

/** The requirement as the API takes it: texts trimmed, and the rows left empty in the editor dropped rather than refused. */
export function cleanRequirement(requirement: Requirement): Requirement {
  return {
    title: requirement.title.trim(),
    problem: requirement.problem.trim(),
    behaviour: requirement.behaviour.trim(),
    acceptanceCriteria: requirement.acceptanceCriteria
      .map((criterion) => ({ scenario: criterion.scenario.trim(), cucumberSection: orNull(criterion.cucumberSection) }))
      .filter((criterion) => criterion.scenario !== ''),
    scope: cleanList(requirement.scope),
    outOfScope: cleanList(requirement.outOfScope),
    policyDecisions: requirement.policyDecisions
      .map((decision) => ({ question: decision.question.trim(), answer: orNull(decision.answer) }))
      .filter((decision) => decision.question !== ''),
    docsToUpdate: cleanList(requirement.docsToUpdate),
  }
}

/** Whether the editor holds anything the saved version does not, once both are cleaned. */
export function requirementChanged(draft: Requirement, saved: Requirement): boolean {
  return JSON.stringify(cleanRequirement(draft)) !== JSON.stringify(cleanRequirement(saved))
}
